import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import api from '@helpers/api';
import { useCommunity } from "contexts/community";
import Parser from '@helpers/parser';
import { AttachMoneyRounded, PixRounded, SellRounded, UndoRounded } from '@mui/icons-material';
import { ReactComponent as MastercardIcon } from '@assets/custom-icons/mastercard.svg';
import { ReactComponent as EloIcon } from '@assets/custom-icons/elo.svg';
import { ReactComponent as VisaIcon } from '@assets/custom-icons/visa.svg';
import Popper from '@fragments/Popper';
import { Table, Text } from '@fragments';
import StatusChip from '@fragments/StatusChip';

export function ChargesTable({ userId, displayTitle = false, variant = 'transparent' }) {
    const { t } = useTranslation('community');

    const [charges, setCharges] = useState([]);
    const [loading, setLoading] = useState(true);
    const popperRef = useRef();

    const { community, loading: communityLoading } = useCommunity();

    useEffect(() => {
        if (userId && !communityLoading) {
            fetchCharges();
        }
    }, [userId, communityLoading]);

    const fetchCharges = async () => {
        try {
            setLoading(true);
            let { data } = await api.get(`charge?member=${userId}`);
            if (data.length > 0) {
                data = data.map(charge => {
                    charge.community = community.get(charge.community);
                    return charge;
                });
            }
            setCharges([...(data || [])]);
        } catch (error) {
            console.error('Error fetching charges:', error);
            setCharges([]);
        } finally {
            setLoading(false);
        }
    };

    const getMethodIcon = (method, cardBrand) => {
        if (method === 'credit') {
            const brand = cardBrand?.toLowerCase() || 'visa';
            return (
                <>
                    {{
                        'visa': <VisaIcon />,
                        'elo': <EloIcon />,
                        'mastercard': <MastercardIcon />
                    }[brand]}
                </>
            );
        } else if (method === 'pix') {
            return <PixRounded />;
        }
        return <AttachMoneyRounded />;
    };

    const formatAmount = (amount, installments, currency) => {
        if (!amount) return Parser.price.format(0, currency);

        if (installments && installments > 1) {
            const installmentAmount = amount / installments;
            return `${Parser.price.format(amount, currency)} (${installments}x ${Parser.price.format(installmentAmount, currency)})`;
        }

        return Parser.price.format(amount, currency);
    };

    const getCardNumber = (charge) => {
        if (charge.method === 'credit' && charge.card?.last_four) {
            return `• ${charge.card.last_four}`;
        }
        return '';
    };

    const columns = [
        {
            id: 'product',
            title: t('tables.charges.product'),
            grow: 1,
            className: 'pl-1',
            customRender: (data) => (
                <>
                    <img
                        src={data.community?.banner || ''}
                        className="community-row-banner"
                    />
                    <Text.span contrast={7} weight={400}>
                        {data.community?.name || 'N/A'}
                    </Text.span>
                </>
            )
        },
        {
            id: 'status',
            title: t('common:fields.status'),
            align: 'center',
            customRender: (data) => {
                // Only the positive status gets a colored pill
                const StatusComponent = data.status === 'paid' ? StatusChip.Success : StatusChip.Default;

                return <StatusComponent text={Parser.payment.chargeStatus(data.status)} />;
            }
        },
        {
            id: 'amount',
            title: t('tables.charges.grossAmount'),
            render: (data) => formatAmount(data.amount, data.installments, data.currency)
        },
        {
            id: 'method',
            title: t('tables.charges.method'),
            align: 'center',
            customRender: (data) => (
                <>
                    {getMethodIcon(data.method, data.card?.brand)}
                    {data.method === 'credit' && (
                        <label className="card-number">
                            {getCardNumber(data)}
                        </label>
                    )}
                </>
            )
        },
        {
            id: 'date',
            title: t('common:fields.date'),
            // Payment date when paid; creation date for pending/canceled charges
            render: (data) => Parser.date.readable(data.timestamps?.paid || data.timestamps?.created)
        }
    ];


    const handleOpenPopper = (event, charge) => {
        const options = [];

        if (charge.status?.toLowerCase() === 'paid') {
            options.push({
                id: 'refund',
                label: t('tables.charges.refund.action'),
                icon: <UndoRounded fontSize="small" />,
                variant: 'warning',
                onClick: () => {
                    window.openConfirm({
                        title: t('tables.charges.refund.action'),
                        subtitle: t('tables.charges.refund.confirmSubtitle'),
                        buttons: [
                            {
                                text: t('tables.charges.refund.confirmButton'),
                                onClick: async () => {
                                    try {
                                        const { data } = await api.delete(`charge/${charge.id}/refund`);

                                        fetchCharges()
                                    } catch (error) {
                                        console.error('Erro ao realizar estorno:', error);
                                    }
                                },
                                variant: "contained",
                                className: "",
                                color: "error",
                            },
                            {
                                text: t('tables.charges.refund.cancelButton'),
                                variant: "text",
                            },
                        ],
                    });
                }
            });
        }

        if (options.length === 0) return;

        popperRef.current.open(options, 'bottom', event.currentTarget);
    };

    return (
        <>
            {
                displayTitle && <Text.h3 value={t('tables.charges.title')} className='mb-2 mt-5' left={<SellRounded />} />
            }
            <Table
                id="community-user-charges-table"
                columns={columns}
                data={charges}
                loading={loading}
                loadingRows={4}
                contained={true}
                emptyMessage={t('tables.charges.empty')}
                actionsComponent={(charge) => (
                    <button
                        className={`action-button ${charge.status?.toLowerCase() !== 'paid' ? 'disabled' : ''}`}
                        onClick={(event) => handleOpenPopper(event, charge)}
                        disabled={charge.status?.toLowerCase() !== 'paid'}
                    >
                        <MoreVertIcon />
                    </button>
                )}
            />
            <Popper controlRef={popperRef} />
        </>
    );
}
